import {ATTENTION_METRIC_NAME, BrandColors, ChartData} from "../../shared/types/common";
import {SortFieldType} from '../my-campaigns-screen/types';
import {SelectItem} from '../../shared/components/select-menu/select-menu.component';
import {DashboardFiltersPayload} from "../../services/dashboard.service";

export type SitePerformanceResponse = {
  sites: string[];
}

export type ContentCategoryResponse = {
  categories: ChartData[];
}

export type SitesFiltersResponse = {
  brands: {
    [key: string]: {
      name: string,
      categories: string[]
    }
  }
}

export type BrandFiltersResponse = {
  name: string;
  logoPath?: string;
  categories: string[];
  competitors: {
    [key: string]: {
      name: string;
      logoPath?: string;
      categories: string[];
    };
  };
  topCompetitorKeys: string[];
}

export type BrandsFiltersResponse = {
  [brandKey: string]: BrandFiltersResponse;
}

export type CompetitionFiltersResponse = {
  brands: BrandsFiltersResponse;
}

export interface MyBrandPerformanceOverTimeResponse {
  performance: ChartData[];
  isUnderDateThreshold: boolean;
}

export enum InventoryQualityTableColumns {
  DOMAIN = 'domain',
  CATEGORY = 'category',
  BRAND_IMPRESSIONS = 'impressions',
  VIEWABILITY = 'viewability',
  ATTENTION = 'attention',
  REFRESH_INTERVAL = 'refreshInterval',
  DENSITY = 'density',
  CLICK_RISK = 'clickRisk',
  CPM = 'cpm',
  CTR = 'ctr'
}

export enum ClickRisk {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High'
}

export enum PerformanceComparisonMetric {
  ATTENTION = 'attention',
  VIEWABILITY = 'viewability',
  CPM = 'cpm',
  DWELL_TIME = 'dwellTime',
  AD_CLUTTER = 'adClutter',
  CPC = 'cpc',
  CTR = 'ctr',
  AD_FATIGUE = 'adFatigue',
  AD_DENSITY = 'adDensity',
  REFRESH_RATE = 'refreshRate',
  IMPRESSIONS = 'impressions',
  IMPRESSIONS_SHARE = 'impressionsShare',
  SPEND = 'spend',
  EFFICIENCY = 'efficiency',
  IMPACT = 'impact',
  SHARE_OF_VOICE = "shareOfVoice"
}

export enum GalleryNavigation {
  NEXT = 'next',
  PREV = 'prev'
}

export type InventoryQualityTableRow = {
  domain: string,
  category: string,
  impressions: number,
  viewability: number,
  attention: number
  refreshInterval: number,
  density: number,
  clickRisk: ClickRisk,
  cpm: number
  ctr?: number;
}

export type InventoryQualityTableResponse = {
  sites: InventoryQualityTableRow[],
  total: number
}

export interface InventoryQualityTablePayload extends DashboardFiltersPayload {
  brands: string[];
  categories: string[];
  sortField?: SortFieldType
  limit?: number;
  offset?: number;
  isAsc?: boolean;
  website?: string;
}

export type BrandsSessionFilters = {
  inventoryQuality?: InventoryQualityTablePayload & { brand?: string },
  creativeGallery?: CreativeGallerySort
}

export type CreativeGalleryBase = {
  brandName: string;
  id: string;
  imgUrl: string;
  attention: number;
  viewability: number;
  cpm: number;
  ctr: number;
  timeInView: number;
  logoPath: string;
}

export type CreativeGalleryDetailed = CreativeGalleryBase & {
  maxImpressions: number;
  minImpressions: number;
  firstSeen: string;
  lastSeen: string;
  size?: string;
}

export type CreativeGalleryResponse = {
  creativesData: CreativeGalleryBase[];
  total: number;
}

export const MyBrandPerformanceMetrics = [
  { key: PerformanceComparisonMetric.SPEND, label: 'Spend' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.DWELL_TIME, label: 'Dwell Time' },
  {key: PerformanceComparisonMetric.IMPRESSIONS, label: 'Impressions' },
];

export const ComparisonMetrics = [
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.VIEWABILITY, label: 'Viewability' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.DWELL_TIME, label: 'Dwell Time' },
  { key: PerformanceComparisonMetric.AD_CLUTTER, label: 'Ad Clutter' }
];

export const PerformanceByTimeMetrics = [
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.DWELL_TIME, label: 'Dwell Time' },
  { key: PerformanceComparisonMetric.AD_CLUTTER, label: 'Ad Clutter' },
  { key: PerformanceComparisonMetric.AD_DENSITY, label: 'Ad Density' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.VIEWABILITY, label: 'Viewability' },
  { key: PerformanceComparisonMetric.IMPRESSIONS_SHARE, label: 'Impression Share per Brand' },
  { key: PerformanceComparisonMetric.EFFICIENCY, label: 'Efficiency' },
  { key: PerformanceComparisonMetric.IMPACT, label: 'Impact' }
];

export const PerformanceByDeviceMetrics = [
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.DWELL_TIME, label: 'Dwell Time' },
  { key: PerformanceComparisonMetric.AD_CLUTTER, label: 'Ad Clutter' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.VIEWABILITY, label: 'Viewability' },
  { key: PerformanceComparisonMetric.EFFICIENCY, label: 'Efficiency' },
  { key: PerformanceComparisonMetric.IMPACT, label: 'Impact' },
];

export const ComingSoonMetrics = [
  { key: PerformanceComparisonMetric.CPC, label: 'CPC (Coming Soon)', disabled: true },
  { key: PerformanceComparisonMetric.CTR, label: 'CTR (Coming Soon)', disabled: true },
  { key: PerformanceComparisonMetric.AD_FATIGUE, label: 'Ad Fatigue (Coming Soon)', disabled: true }
];

export const SpendEfficiencyYAxisMetrics = [
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.CTR, label: 'CTR' }
];

export const SpendEfficiencyXAxisMetrics = [
  { key: PerformanceComparisonMetric.SPEND, label: 'Estimated Spend' },
  { key: PerformanceComparisonMetric.IMPRESSIONS, label: 'Impressions' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.CTR, label: 'CTR' }
];

export type CreativeGallerySortField = 'impressions' | 'cpm' | 'ctr' | 'attention' | 'firstSeen' | 'lastSeen';

export interface CreativeGalleryPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
  sortField?: CreativeGallerySortField;
  limit?: number;
  offset?: number;
  isAsc?: boolean;
  minImpressions?: number;
}

export interface CreativeCardPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
  creativeId: string;
}

export type CreativeGallerySort = {
  sortField: CreativeGallerySortField,
  isAsc: boolean
}

const sortFieldLabels: Record<CreativeGallerySortField, string> = {
  impressions: 'Impressions',
  cpm: 'CPM',
  ctr: 'CTR',
  attention: ATTENTION_METRIC_NAME,
  firstSeen: 'First Seen',
  lastSeen: 'Last Seen'
};

const sortFields: CreativeGallerySortField[] = ['impressions', 'cpm', 'ctr', 'attention', 'firstSeen', 'lastSeen'];

export const CREATIVE_GALLERY_SORT_OPTIONS: SelectItem<CreativeGallerySort>[] = sortFields.flatMap((field) => [
  {
    key: { sortField: field, isAsc: false },
    label: `${sortFieldLabels[field]} (Descending)`
  },
  {
    key: { sortField: field, isAsc: true },
    label: `${sortFieldLabels[field]} (Ascending)`
  }
]);

export type MyBrandDataFetchResult = {
  overview?: BrandOverviewResponse
  sitePerformance: SitePerformanceResponse,
  contentCategoryBreakDown: ContentCategoryResponse,
  tableData: InventoryQualityTableResponse,
  creativeGallery: CreativeGalleryResponse,
  performanceMapData: PerformanceMapResponse,
  performanceOverTime: MyBrandPerformanceOverTimeResponse,
}

export interface BrandOverviewResponse {
  minImpressions: number;
  maxImpressions: number;
  cpm: number;
  dwellTime: number;
  viewability: number;
  attention: number;
  clickRisk: ClickRisk;
  attentionTrend: number;
  dwellTimeTrend: number;
  ctr?: number;
  ctrTrend?: number;
  /** @deprecated Use `ctr` instead — remove after full backend migration */
  clutter?: number;
  /** @deprecated Use `ctrTrend` instead — remove after full backend migration */
  clutterTrend?: number;
}

export interface PerformanceMapResponse {
  performance?: {
    geoCode: string,
    [PerformanceComparisonMetric.ATTENTION]?: number,
    [PerformanceComparisonMetric.DWELL_TIME]?: number,
    [PerformanceComparisonMetric.REFRESH_RATE]?: number,
    [PerformanceComparisonMetric.AD_CLUTTER]?: number,
    [PerformanceComparisonMetric.CPM]?: number,
    [PerformanceComparisonMetric.SPEND]?: number,
    [PerformanceComparisonMetric.IMPRESSIONS]?: number
  }[];
  myBrand?: {
    brand: string,
    geoCode: string,
    [PerformanceComparisonMetric.ATTENTION]?: number,
    [PerformanceComparisonMetric.DWELL_TIME]?: number,
    [PerformanceComparisonMetric.REFRESH_RATE]?: number,
    [PerformanceComparisonMetric.AD_CLUTTER]?: number,
    [PerformanceComparisonMetric.CPM]?: number,
    [PerformanceComparisonMetric.SPEND]?: number,
    [PerformanceComparisonMetric.IMPRESSIONS]?: number
  }[];
  myCompetitors?: {
    brand: string,
    geoCode: string,
    [PerformanceComparisonMetric.ATTENTION]?: number,
    [PerformanceComparisonMetric.DWELL_TIME]?: number,
    [PerformanceComparisonMetric.REFRESH_RATE]?: number,
    [PerformanceComparisonMetric.AD_CLUTTER]?: number,
    [PerformanceComparisonMetric.CPM]?: number,
    [PerformanceComparisonMetric.SPEND]?: number,
    [PerformanceComparisonMetric.IMPRESSIONS]?: number
  }[];
}

export const PerformanceMapMetrics: SelectItem<PerformanceComparisonMetric>[] = [
  { key: PerformanceComparisonMetric.SPEND, label: 'Spend' },
  { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME },
  { key: PerformanceComparisonMetric.DWELL_TIME, label: 'Dwell Time' },
  { key: PerformanceComparisonMetric.REFRESH_RATE, label: 'Refresh Rate' },
  { key: PerformanceComparisonMetric.AD_CLUTTER, label: 'Ad Clutter' },
  { key: PerformanceComparisonMetric.CPM, label: 'CPM' },
  { key: PerformanceComparisonMetric.IMPRESSIONS, label: 'Impressions' },
  { key: PerformanceComparisonMetric.EFFICIENCY, label: 'Efficiency' },
  { key: PerformanceComparisonMetric.IMPACT, label: 'Impact' }
]

export enum PerformanceMapType {
  WORLDWIDE = 'worldwide',
  US = 'us'
}

export interface ContentCategoryPayload extends DashboardFiltersPayload {
  brands: string[];
}

export interface SitePerformancePayload extends DashboardFiltersPayload {
  brands: string[];
}

export interface PerformanceMapPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
  minImpressions?: number;
}

export interface OverviewPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
}

export interface PerformanceOverTimePayload extends DashboardFiltersPayload {
  brandKey: string;
  metric: PerformanceComparisonMetric;
}

export const GENDER_CHART_COLORS = {
  men:    '#4a7bca',
  women:  '#d45b93',
  noData: '#9ca3af',
};

export type MyBrandAudienceGenderPayload = {
  brandsKeys: string[];
} & DashboardFiltersPayload;

export type MyBrandAudienceGenderResponse = {
  menPercentage: number | null;
  womenPercentage: number | null;
};
